import { NavLink } from 'react-router-dom';
import { ClipboardList, Columns3, Cpu, LayoutDashboard, LogOut, MonitorSmartphone, Package, Settings, Shapes, TrendingUp, UserCog, Users, Wrench, Warehouse } from 'lucide-react';
import { ROLE_LABELS, type Permission } from '@oficina/shared';
import { useAuth } from '@/hooks/use-auth';
import { initials } from '@/lib/format';
import { cn } from '@/lib/utils';

type NavItem = { to: string; label: string; icon: typeof Users; end?: boolean; permission?: Permission };

/** Grupos do menu, separados por linha e título. Grupos sem itens permitidos não aparecem. */
const GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Ordem de serviço',
    items: [
      { to: '/', label: 'Visão geral', icon: LayoutDashboard, end: true },
      { to: '/ordens', label: 'Ordens de serviço', icon: ClipboardList },
      { to: '/bancada', label: 'Bancada', icon: Columns3 },
    ],
  },
  {
    title: 'Cadastros',
    items: [
      { to: '/clientes', label: 'Clientes', icon: Users },
      { to: '/equipamentos', label: 'Equipamentos', icon: MonitorSmartphone },
      { to: '/servicos', label: 'Serviços', icon: Wrench },
      { to: '/categorias', label: 'Categorias', icon: Shapes, permission: 'categories.manage' },
    ],
  },
  {
    title: 'Estoque',
    items: [
      { to: '/produtos', label: 'Produtos', icon: Package },
      { to: '/componentes', label: 'Componentes', icon: Cpu },
      { to: '/estoque', label: 'Controle de estoque', icon: Warehouse, permission: 'stock.adjust' },
    ],
  },
  {
    title: 'Financeiro',
    items: [{ to: '/financeiro', label: 'Financeiro', icon: TrendingUp, permission: 'finance.view' }],
  },
  {
    title: 'Configurações',
    items: [
      { to: '/usuarios', label: 'Usuários', icon: UserCog, permission: 'users.manage' },
      { to: '/configuracoes', label: 'Configurações', icon: Settings },
    ],
  },
];

/**
 * Marca do FixTech. `stacked` mostra o logotipo completo (tela de login);
 * o padrão é o ícone com o nome ao lado (menu lateral).
 */
export function BrandMark({ className, stacked }: { className?: string; stacked?: boolean }) {
  if (stacked) {
    return (
      <div className={cn('flex flex-col items-center gap-2 text-center', className)}>
        <img src="/fixtech-logo.png" alt="FixTech" width={150} height={147} className="h-auto w-[150px]" />
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Gestão de Assistência Técnica</span>
      </div>
    );
  }
  return (
    <div className={cn('flex items-center gap-[11px]', className)}>
      <img src="/fixtech-icon.png" alt="" width={60} height={60} className="h-12 w-12 shrink-0" />
      <div className="min-w-0 leading-none">
        <div className="font-display text-[21px] font-extrabold tracking-[-0.03em]">
          Fix<span className="text-[#12d9a0]">Tech</span>
        </div>
        <div className="mt-1 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-sidebar-muted">Gestão de Assistência Técnica</div>
      </div>
    </div>
  );
}

/** Menu lateral escuro do protótipo. No celular é exibido dentro de um "Sheet" (drawer). */
export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user, logout, can } = useAuth();

  const link = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'mb-1 flex w-full items-center gap-3 rounded-[9px] px-3 py-[11px] text-sm font-semibold transition-colors',
          isActive ? 'bg-primary text-white' : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-white',
        )
      }
    >
      <item.icon className="h-[17px] w-[17px]" aria-hidden />
      {item.label}
    </NavLink>
  );

  return (
    <div className="flex h-full flex-col bg-sidebar px-4 py-6 text-[#f2f6f3]">
      <div className="relative">
        <span className="absolute -top-3 right-0 text-[10px] text-sidebar-muted" aria-label={`Versão do aplicativo ${__APP_VERSION__}`}>
          v{__APP_VERSION__}
        </span>
        <NavLink to="/" onClick={onNavigate} aria-label="FixTech — Página inicial" className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <BrandMark className="px-2 pb-7" />
        </NavLink>
      </div>
      <nav aria-label="Menu principal" className="sidebar-scroll min-h-0 flex-1 pr-1">
        {GROUPS.map((group) => ({ ...group, items: group.items.filter((i) => !i.permission || can(i.permission)) }))
          .filter((group) => group.items.length > 0)
          .map((group, index) => (
            <section
              key={group.title}
              aria-label={group.title}
              className={cn(index > 0 && 'mt-3 border-t border-sidebar-border pt-3')}
            >
              <div className="px-3 pb-2 pt-1 text-[10px] uppercase tracking-[0.11em] text-sidebar-muted">{group.title}</div>
              {group.items.map(link)}
            </section>
          ))}
      </nav>
      <div className="mt-4 flex items-center gap-[11px] border-t border-sidebar-border px-2 pt-4">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#dceee5] text-sm font-bold text-[#276249]" aria-hidden>
          {initials(user?.name)}
        </div>
        <div className="min-w-0 flex-1">
          <b className="block truncate text-xs">{user?.name}</b>
          <small className="text-[11px] text-[#a5b0ad]">{user ? ROLE_LABELS[user.role] : ''}</small>
        </div>
        <button
          type="button"
          onClick={() => void logout()}
          className="rounded-lg p-2 text-sidebar-foreground transition hover:bg-sidebar-hover hover:text-white"
          title="Sair"
          aria-label="Sair"
        >
          <LogOut className="h-[17px] w-[17px]" />
        </button>
      </div>
    </div>
  );
}
